import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import ShieldIcon from 'components/material-symbol/icons/Shield'
import Error from 'components/error'
import StripePaymentElementsForm from '../stripe-payment-elements-form'
import useReviewAndPay from '../steps/use-review-and-pay'
import { isRegistrationFullError } from '../../utils/registration-full'
import useStyles from './styles'

const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    borderRadius: '12px',
    colorPrimary: '#00234B',
    colorText: '#172024',
  },
}

const participantSummary = participants =>
  participants
    .map(p => {
      const name = `${p.firstName || ''} ${p.lastName || ''}`.trim()
      return p.type ? `${name} (${p.type})` : name
    })
    .filter(Boolean)
    .join(', ')

const ReviewStep = ({
  groupEvent,
  handlePrev,
  handleNext,
  paymentRequired,
  shouldFetchClientSecret,
  setShouldFetchClientSecret,
  shouldShowWaiver,
  stripePaymentFormRef,
  marketingOptInChecked,
  onRegistrationFull,
}) => {
  const classes = useStyles()
  const {
    participants,
    phoneNumber,
    contactAttributes,
    clientSecret,
    loading,
    error,
    success,
    message,
    handleClick,
  } = useReviewAndPay({
    groupEvent,
    handleNext,
    paymentRequired,
    shouldFetchClientSecret,
    setShouldFetchClientSecret,
    shouldShowWaiver,
    stripePaymentFormRef,
  })

  const registrationFull =
    isRegistrationFullError(error) || isRegistrationFullError(message)

  useEffect(() => {
    if (registrationFull && onRegistrationFull) {
      onRegistrationFull()
    }
  }, [registrationFull, onRegistrationFull])

  const submitButtonText = useMemo(() => {
    if (!success) {
      return message || 'Unable to proceed'
    }
    if (shouldShowWaiver) {
      return 'Continue'
    }
    return 'Complete Registration'
  }, [success, message, shouldShowWaiver])

  if (loading) {
    return <LinearProgress />
  }

  if (error && !registrationFull) {
    return <Error error={error} />
  }

  const contactName = [contactAttributes.firstName, contactAttributes.lastName]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes.card}>
      <div className={classes.detailsCard}>
        <div className={classes.detailsHeader}>
          <Typography
            variant="h6"
            component="h2"
            className={classes.detailsTitle}
          >
            Your details
          </Typography>
          <Button className={classes.editLink} onClick={handlePrev}>
            Edit
          </Button>
        </div>
        <dl className={classes.detailsGrid}>
          <div>
            <dt className={classes.detailLabel}>Name</dt>
            <dd className={classes.detailValue}>{contactName}</dd>
          </div>
          <div>
            <dt className={classes.detailLabel}>Participants</dt>
            <dd className={classes.detailValue}>
              {participantSummary(participants)}
            </dd>
          </div>
          {contactAttributes.email && (
            <div>
              <dt className={classes.detailLabel}>Email</dt>
              <dd className={classes.detailValue}>{contactAttributes.email}</dd>
            </div>
          )}
          {phoneNumber && (
            <div>
              <dt className={classes.detailLabel}>Phone</dt>
              <dd className={classes.detailValue}>{phoneNumber}</dd>
            </div>
          )}
        </dl>
      </div>

      <div className={classes.securePanel}>
        <ShieldIcon aria-hidden="true" />
        <div>
          <Typography variant="body1" className={classes.securePanelTitle}>
            Secure payment
          </Typography>
          <p className={classes.securePanelBody}>
            Your credit card information is not being stored in our system.
          </p>
        </div>
      </div>

      <StripePaymentElementsForm
        ref={stripePaymentFormRef}
        clientSecret={clientSecret}
        secretLoading={loading}
        groupEvent={groupEvent}
        marketingOptInChecked={marketingOptInChecked}
        showIntro={false}
        appearance={STRIPE_APPEARANCE}
      />

      <div className={classes.footer}>
        <Button className={classes.textButton} onClick={handlePrev}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={classes.primaryButton}
          onClick={handleClick}
          disabled={!success}
        >
          {submitButtonText}
        </Button>
      </div>
    </div>
  )
}

ReviewStep.propTypes = {
  groupEvent: PropTypes.object.isRequired,
  handlePrev: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  paymentRequired: PropTypes.bool,
  shouldFetchClientSecret: PropTypes.bool,
  setShouldFetchClientSecret: PropTypes.func,
  shouldShowWaiver: PropTypes.bool,
  stripePaymentFormRef: PropTypes.object.isRequired,
  marketingOptInChecked: PropTypes.bool,
  onRegistrationFull: PropTypes.func,
}

export default ReviewStep
